import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
]

const RTL_LANGUAGES = ['ar']

export default function LanguageSelector() {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0]

  // Keep the document language and text direction in sync (RTL for Arabic).
  useEffect(() => {
    document.documentElement.lang = i18n.language
    document.documentElement.dir = RTL_LANGUAGES.includes(i18n.language) ? 'rtl' : 'ltr'
  }, [i18n.language])

  // Close the dropdown when clicking outside of it.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function changeLang(langCode: string) {
    i18n.changeLanguage(langCode)
    try {
      localStorage.setItem('lang', langCode)
      const params = new URLSearchParams(window.location.search)
      params.set('lang', langCode)
      window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`)
    } catch {
      /* localStorage / history may be unavailable */
    }
    setOpen(false)
  }

  return (
    <div
      ref={ref}
      dir='ltr'
      className='absolute top-4 right-4 z-50 text-left'
    >
      <button
        onClick={() => setOpen(!open)}
        aria-label='Select language'
        aria-haspopup='listbox'
        aria-expanded={open}
        className='flex items-center justify-center text-2xl leading-none rounded-md px-2 py-1 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40 cursor-pointer'
      >
        <span>{currentLang.flag}</span>
      </button>

      {open && (
        <ul
          role='listbox'
          className='absolute right-0 mt-2 min-w-[11rem] max-h-[70vh] overflow-auto rounded-md bg-zinc-900 border border-zinc-700 shadow-xl p-1 z-50'
        >
          {LANGUAGES.map((lang) => (
            <li key={lang.code}>
              <button
                role='option'
                aria-selected={lang.code === currentLang.code}
                onClick={() => changeLang(lang.code)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded text-sm text-gray-200 hover:bg-zinc-800 cursor-pointer ${
                  lang.code === currentLang.code ? 'bg-zinc-800' : ''
                }`}
              >
                <span className='text-base'>{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
